import { useEffect, useState } from "react";
import { _IncidentReport, _single } from "../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../core/services/dataBean/useDataBean";
import { toast } from "react-toastify";
import "./TracingIncidentReport.css";
import { CardTaps } from "../../../theme/Component/CardTaps";
import { VisorDocumentBase64 } from "./VisorDocumentsBase64/VisorDocumentBase64";

interface AttachedDocument {
  id: number;
  filename: string;
  content?: string;
  content_type?: string;
}

interface SelectedDocument {
  base64: string;
  mimeType: string;
  filename: string;
  id: number;
}

interface TracingIncidentReportProps {
  IncidentCode: string;
  incidentData?: any;
  documentsData?: AttachedDocument[];
  autoFetch?: boolean;
}

export const TracingIncidentReport = ({ IncidentCode, incidentData, documentsData, autoFetch = true }: TracingIncidentReportProps) => {
  const {
    bean,
    setBean,
    instance,
    lista: listaDocuments,
    setLista: setListaDocuments
  } = useDataBean(_IncidentReport);

  const [indexTap, setIndexTap] = useState<number>(0);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState<SelectedDocument | null>(null);

  const hasExternalIncidentData = incidentData !== undefined && incidentData !== null;
  const hasExternalDocumentsData = Array.isArray(documentsData);

  useEffect(() => {
    setDocumentoSeleccionado(null);
  }, [IncidentCode]);

  useEffect(() => {
    if (!IncidentCode) return;

    if (hasExternalIncidentData) {
      setBean(incidentData);
      return;
    }

    if (autoFetch) {
      getLista(IncidentCode);
    }
  }, [IncidentCode, incidentData, autoFetch]);

  useEffect(() => {
    if (hasExternalDocumentsData) {
      setListaDocuments(documentsData);
    }
  }, [documentsData]);

  useEffect(() => {
    setDocumentoSeleccionado(null);

    if (indexTap !== 3) return;
    if (!IncidentCode) return;

    if (hasExternalDocumentsData) {
      setListaDocuments(documentsData ?? []);
      return;
    }

    if (autoFetch) {
      getListaDocument();
    }
  }, [indexTap, IncidentCode, documentsData, autoFetch]);

  const getLista = (codeId: string) => {
    instance
      .getIncidentReportByIncidentCode(_single.getAccountID(), codeId)
      .then((resp) => setBean(resp))
      .catch((err) => toast.error(err));
  };

  const getListaDocument = () => {
    instance
      .getIssuesFiles(Number(IncidentCode))
      .then((resp) => {
        if (resp.Files) {
          setListaDocuments(JSON.parse(resp.Files));
        } else {
          setListaDocuments([]);
          toast.error("No se pudo cargar los documentos");
        }
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  const getDocumentInfo = (id: number) => {
    instance
      .getIssuesFilesById(Number(IncidentCode), id)
      .then((resp) => {
        if (resp.Files) {
          const files = JSON.parse(resp.Files) || [];

          if (files.length === 1) {
            const file = files[0];
            setDocumentoSeleccionado({
              base64: file.content,
              mimeType: file.content_type?.split(";")[0],
              filename: file.filename,
              id: file.id
            });
          }
        }
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  const openDocument = (item: AttachedDocument) => {
    if (item.content) {
      setDocumentoSeleccionado({
        base64: item.content,
        mimeType: item.content_type?.split(";")[0] || "",
        filename: item.filename,
        id: item.id
      });
      return;
    }

    getDocumentInfo(item.id);
  };

  const taps = [
    { id: 0, name: "Reporte", disable: false },
    { id: 1, name: "Notas", disable: false },
    { id: 2, name: "Historial", disable: false },
    { id: 3, name: "Documentos Adjuntos", disable: false }
  ];

  return (
    <div className="report-container">
      <CardTaps index={indexTap} taps={taps} updateIndex={setIndexTap}>
        <div className="row">
          {indexTap === 0 && (
            <div className="col">
              <header className="report-header">
                <h2>
                  Reporte de Incidente #{bean?.IncidenceCode ?? "—"}
                </h2>
                <span className={`status ${bean?.StatusName ?? "loading"}`}>
                  {bean?.StatusName?.toUpperCase() ?? "CARGANDO"}
                </span>
              </header>

              <section className="report-section">
                <h3>Descripción</h3>
                <p>{bean?.Description ?? "Sin descripción"}</p>
              </section>

              <section className="report-section grid">
                <div>
                  <strong>Fecha de creación:</strong>
                  <p>
                    {bean?.Since
                      ? new Date(bean.Since).toLocaleString()
                      : "—"}
                  </p>
                </div>

                <div>
                  <strong>Última actualización:</strong>
                  <p>
                    {bean?.updated_at
                      ? new Date(bean.updated_at).toLocaleString()
                      : "—"}
                  </p>
                </div>

                <div>
                  <strong>Resolución:</strong>
                  <p>{bean?.resolutionName ?? "—"}</p>
                </div>
              </section>
            </div>
          )}

          {indexTap === 1 && (
            <div className="col">
              <section className="report-section">
                <h3>Notas</h3>
                <ul className="timeline">
                  {bean?.notes?.length ? (
                    bean.notes.map((note: any, index: number) => (
                      <li key={index}>
                        <span>
                          {note.update
                            ? new Date(note.update).toLocaleString()
                            : "—"}
                        </span>
                        <p>{note.text ?? "Sin detalle"}</p>

                        {note.attachNote?.length > 0 && (
                          <ul className="note-attachments">
                            {note.attachNote.map((file: any, i: number) => {
                              const props = file.DataBeanProperties;

                              return (
                                <li key={i}>
                                  <a
                                    href="#"
                                    className="doc-link doc-link-with-icon"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (props.IDFile) getDocumentInfo(props.IDFile);
                                    }}
                                  >
                                    <i className="ri-attachment-2 doc-icon" />
                                    <span>{props.FileName}</span>
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    ))
                  ) : (
                    <li>No hay notas registradas</li>
                  )}
                </ul>
              </section>
            </div>
          )}

          {indexTap === 2 && (
            <div className="col">
              <section className="report-section">
                <h3>Historial</h3>
                <ul className="timeline">
                  {bean?.history?.length ? (
                    bean.history.map((item: any, index: number) => (
                      <li key={index}>
                        <span>
                          {item.created
                            ? new Date(item.created).toLocaleString()
                            : "—"}
                        </span>
                        <p>
                          <strong>{item.user ?? "Sistema"}</strong>:{" "}
                          {item.message ?? "—"}
                        </p>
                      </li>
                    ))
                  ) : (
                    <li>No hay historial</li>
                  )}
                </ul>
              </section>
            </div>
          )}

          {indexTap === 3 && (
            <div className="col">
              <h3>Documentos Adjuntos</h3>

              <div className="col-md-12">
                <div
                  className="list-group"
                  style={{ maxHeight: "52vh", overflow: "auto" }}
                >
                  {Array.isArray(listaDocuments) && listaDocuments.length > 0 ? (
                    listaDocuments.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center pointer"
                        onClick={() => openDocument(item)}
                      >
                        <span
                          style={{
                            width: "80%",
                            overflow: "hidden",
                            textWrap: "nowrap"
                          }}
                        >
                          <i className="ri-article-line fs-2 align-middle me-2 cursor"></i>
                          {item.id} - {item.filename}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="list-group-item">
                      No hay documentos adjuntos
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardTaps>

      <VisorDocumentBase64
        modalData={documentoSeleccionado}
        idDocument={documentoSeleccionado?.id}
      />
    </div>
  );
};